import type { AxiosInstance } from 'axios';

export interface EarningReport {
  totalSalesAllTimePaise: number;
  salesThisMonthPaise: number;
  payoutsAllTimePaise: number;
  payoutsThisMonthPaise: number;
  totalCategory: number;
  totalBrands: number;
  netSales: Array<{ label: string; amountPaise: number }>;
  payoutsBreakdown: Array<{ label: string; amountPaise: number }>;
}

export interface ProductSaleRow {
  productName: string;
  numSold: number;
}

export interface StockRow {
  productTitle: string;
  sku: string;
  onHand: number;
  reserved: number;
  available: number;
}

export interface CommissionRow {
  id: string;
  orderGroupId: string;
  orderNumber: string;
  vendorId: string;
  bps: number;
  amountPaise: number;
  status: string;
  createdAt: string;
}

export interface WalletRechargeRow {
  id: string;
  userId: string;
  customer: string;
  bucket: string;
  amountPaise: number;
  remainingPaise: number;
  status: string;
  createdAt: string;
}

export interface WishlistRow {
  id: string;
  userId: string;
  productName: string;
  sku: string;
  customer: string;
  addedAt: string;
}

export interface SearchRow {
  query: string;
  count: number;
  lastSearchedAt: string;
}

export interface DateRangeParams {
  from?: string;
  to?: string;
}

const unwrap = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

export function reportEndpoints(http: AxiosInstance) {
  /** Build the export CSV URL for a given report path + current date range */
  function exportUrl(
    path: string,
    range?: DateRangeParams,
    extra?: Record<string, string>,
  ): string {
    const base = (http.defaults.baseURL ?? '') + path;
    const params = new URLSearchParams();
    if (range?.from) params.set('from', range.from);
    if (range?.to) params.set('to', range.to);
    if (extra) Object.entries(extra).forEach(([k, v]) => params.set(k, v));
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  return {
    earnings: (range?: DateRangeParams) =>
      http.get<EarningReport>('/admin/reports/earnings', { params: range }).then((r) => r.data),
    inHouseSales: (categoryId?: string, range?: DateRangeParams) =>
      unwrap<ProductSaleRow[]>(
        http.get('/admin/reports/in-house-sales', {
          params: { ...(categoryId ? { categoryId } : {}), ...range },
        }),
      ),
    sellerSales: (categoryId?: string, range?: DateRangeParams) =>
      unwrap<ProductSaleRow[]>(
        http.get('/admin/reports/seller-sales', {
          params: { ...(categoryId ? { categoryId } : {}), ...range },
        }),
      ),
    stock: () => unwrap<StockRow[]>(http.get('/admin/reports/stock')),
    wishlist: (range?: DateRangeParams) =>
      unwrap<WishlistRow[]>(http.get('/admin/reports/wishlist', { params: range })),
    searches: (range?: DateRangeParams) =>
      unwrap<SearchRow[]>(http.get('/admin/reports/searches', { params: range })),
    commissions: (range?: DateRangeParams) =>
      unwrap<CommissionRow[]>(http.get('/admin/reports/commissions', { params: range })),
    walletRecharges: (range?: DateRangeParams) =>
      unwrap<WalletRechargeRow[]>(http.get('/admin/reports/wallet-recharges', { params: range })),
    categories: () =>
      unwrap<Array<{ id: string; name: string }>>(http.get('/admin/reports/categories')),

    // Export URL builders — call window.open(url) in the browser
    exportEarningsUrl: (range?: DateRangeParams) =>
      exportUrl('/admin/reports/earnings/export.csv', range),
    exportInHouseSalesUrl: (categoryId?: string, range?: DateRangeParams) =>
      exportUrl(
        '/admin/reports/in-house-sales/export.csv',
        range,
        categoryId ? { categoryId } : {},
      ),
    exportSellerSalesUrl: (categoryId?: string, range?: DateRangeParams) =>
      exportUrl('/admin/reports/seller-sales/export.csv', range, categoryId ? { categoryId } : {}),
    exportStockUrl: () => exportUrl('/admin/reports/stock/export.csv'),
    exportCommissionsUrl: (range?: DateRangeParams) =>
      exportUrl('/admin/reports/commissions/export.csv', range),
    exportWalletRechargesUrl: (range?: DateRangeParams) =>
      exportUrl('/admin/reports/wallet-recharges/export.csv', range),
    exportWishlistUrl: (range?: DateRangeParams) =>
      exportUrl('/admin/reports/wishlist/export.csv', range),
    exportSearchesUrl: (range?: DateRangeParams) =>
      exportUrl('/admin/reports/searches/export.csv', range),
  };
}

export type ReportEndpoints = ReturnType<typeof reportEndpoints>;
